import React from 'react';
import {
  TRUST_CENTER_DOCS,
  TRUST_CENTER_INTRO,
  TRUST_CENTER_MISSION,
} from './trustCenterContent';

// Trust Center landing page.
// Renders an introductory overview of our commitments followed by a grid of
// document cards. Cards are driven entirely by TRUST_CENTER_DOCS, so adding a
// future document (Research Publications, Accessibility Statement, etc.) makes
// it appear here automatically with no layout changes.
export default function TrustCenter({ onOpenDoc, onBack }) {
  const openDoc = (slug) => {
    if (onOpenDoc) onOpenDoc(slug);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-950 text-white">
        <div
          aria-hidden="true"
          className="absolute -top-24 -left-24 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-24 -right-24 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          {onBack && (
            <button
              onClick={onBack}
              className="block mb-6 text-sm text-indigo-200 hover:text-white font-semibold"
            >
              &larr; Back
            </button>
          )}
          <span className="inline-block px-3 py-1 mb-4 text-[10px] sm:text-xs font-semibold tracking-wider uppercase bg-white/10 border border-white/20 rounded-full text-indigo-100 backdrop-blur">
            Transparency &amp; Trust
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            {TRUST_CENTER_INTRO.title}
          </h1>
          <p className="text-lg sm:text-xl text-amber-200 font-medium mb-5">
            {TRUST_CENTER_INTRO.tagline}
          </p>
          {TRUST_CENTER_INTRO.paragraphs.map((p, i) => (
            <p
              key={i}
              className="text-sm sm:text-base text-indigo-100/90 leading-relaxed max-w-3xl"
            >
              {p}
            </p>
          ))}

          {/* Commitment chips */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-7">
            {TRUST_CENTER_INTRO.commitments.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm font-medium bg-white/10 border border-white/15 rounded-full backdrop-blur"
              >
                <span aria-hidden="true">{c.icon}</span>
                {c.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Philosophy — the "why" behind our approach */}
      {TRUST_CENTER_MISSION && (
        <section className="border-b border-gray-100 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <div className="max-w-3xl">
              <span className="inline-block text-xs sm:text-sm font-semibold uppercase tracking-wider text-indigo-600 mb-3">
                Why We Build This Way
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">
                {TRUST_CENTER_MISSION.title}
              </h2>
              <div className="space-y-4">
                {TRUST_CENTER_MISSION.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    className="text-sm sm:text-base text-gray-700 leading-relaxed"
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Document cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Our Documents
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Read any document below, or download the PDF for your records.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {TRUST_CENTER_DOCS.map((doc) => (
            <button
              key={doc.slug}
              onClick={() => openDoc(doc.slug)}
              className="text-left bg-white rounded-2xl p-6 border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="text-3xl" aria-hidden="true">
                  {doc.icon}
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${
                    doc.type === 'legal'
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {doc.type === 'legal' ? 'Legal' : 'Educational'}
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1.5">
                {doc.cardTitle}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {doc.cardDescription}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  Updated {doc.lastUpdated}
                </span>
                <span className="text-sm font-semibold text-indigo-600">
                  Read &rarr;
                </span>
              </div>
            </button>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-10 text-center max-w-2xl mx-auto">
          Additional documents &mdash; such as research publications, validation
          studies, and an accessibility statement &mdash; will be added here over time.
        </p>
      </section>
    </div>
  );
}
